import { useEffect, useRef, useState } from "react";
import { authClient } from "./auth";
import type { ApiResponse, JournalEntry, Mood } from "@smrti/types";
import { DayView, localDateISO } from "./DayView";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { JournalQueryProvider, journalKey } from "./JournalQueryProvider";

const API_BASE = import.meta.env.VITE_API_URL?.replace(/\/$/, "") ?? "";

const MOODS: { value: Mood; label: string }[] = [
  { value: "great", label: "Great" },
  { value: "good", label: "Good" },
  { value: "okay", label: "Okay" },
  { value: "bad", label: "Bad" },
  { value: "awful", label: "Awful" },
];

function App() {
  const auth = authClient.useSession();
  // Account changes also arrive through session refreshes, not only Sign out.
  // Keep registration mounted across anonymous-account creation.
  const identity = auth.data && !auth.data.user.isAnonymous ? auth.data.user.id : "auth";
  return (
    <JournalQueryProvider key={identity}>
      <SessionApp auth={auth} />
    </JournalQueryProvider>
  );
}

function SessionApp({ auth }: { auth: ReturnType<typeof authClient.useSession> }) {
  const { data: session, isPending, refetch: refetchSession } = auth;
  const queryClient = useQueryClient();
  const entryKey = journalKey(API_BASE, session?.user.id ?? "auth");
  const saveEntry = useMutation({
    mutationFn: async ({
      id,
      fields,
    }: {
      id?: string;
      fields: { title: string; content: string; mood?: Mood | null };
    }) => {
      const r = await fetch(`${API_BASE}/api/entries${id ? `/${id}` : ""}`, {
        method: id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(fields),
      });
      const body = (await r.json()) as ApiResponse<JournalEntry>;
      if (!r.ok || !body.ok) throw new Error(body.ok ? "Couldn’t save entry." : body.error);
      return body.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: entryKey }),
  });
  const deleteEntry = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`${API_BASE}/api/entries/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const body = (await r.json()) as ApiResponse<{ deleted: true }>;
      if (!r.ok || !body.ok) throw new Error(body.ok ? "Couldn’t delete entry." : body.error);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: entryKey }),
  });
  const [error, setError] = useState<string | null>(null);
  const [authBusy, setBusy] = useState(false);
  const busy = authBusy || saveEntry.isPending || deleteEntry.isPending;
  const [view, setView] = useState<"new" | "edit" | "detail" | "day">("day");
  const [day, setDay] = useState(localDateISO);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const main = useRef<HTMLElement>(null);
  useEffect(() => {
    main.current?.querySelector<HTMLElement>("h2")?.focus();
  }, [view]);

  // Registration state
  const [registering, setRegistering] = useState(false);
  const [displayName, setDisplayName] = useState("");

  // New entry form state
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [mood, setMood] = useState<Mood | "">("");

  async function handleRegister() {
    if (!displayName.trim()) return;
    setBusy(true);
    setRegistering(true);
    setError(null);
    try {
      const result = await authClient.signIn.anonymous();
      if (result.error) throw new Error(String(result.error.message ?? result.error.statusText));

      const pk = await authClient.passkey.addPasskey({ name: displayName.trim() });
      if (pk?.error) {
        await authClient.deleteAnonymousUser();
        await authClient.signOut();
        throw new Error(String(pk.error.message ?? pk.error.statusText));
      }

      const r = await fetch(`${API_BASE}/api/complete-registration`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: displayName.trim() }),
      });
      const body = (await r.json()) as { ok: boolean; error?: string };
      if (!body.ok) {
        await authClient.deleteAnonymousUser();
        await authClient.signOut();
        throw new Error(body.error ?? "failed to complete registration");
      }

      await refetchSession();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
      setRegistering(false);
    }
  }

  async function handleCompleteRegistration() {
    if (!displayName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/complete-registration`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: displayName.trim() }),
      });
      const body = (await r.json()) as { ok: boolean; error?: string };
      if (!body.ok) throw new Error(body.error ?? "failed to complete registration");
      await refetchSession();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleSignIn() {
    setBusy(true);
    setError(null);
    try {
      const result = await authClient.signIn.passkey();
      if (result?.error) throw new Error(String(result.error.message ?? result.error.statusText));
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleSignOut() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await authClient.signOut();
    setSelectedEntry(null);
    setTitle("");
    setContent("");
    setMood("");
    setDay(localDateISO());
    setError(null);
    setView("day");
  }

  async function handleSave() {
    if (!title.trim() || !content.trim()) return;
    setError(null);
    try {
      const editing = view === "edit" && selectedEntry;
      const saved = await saveEntry.mutateAsync({
        id: editing ? editing.id : undefined,
        fields: {
          title,
          content,
          ...(editing ? { mood: mood || null } : mood ? { mood } : {}),
        },
      });
      setTitle("");
      setContent("");
      setMood("");
      setDay(localDateISO(new Date(saved.createdAt)));
      setSelectedEntry(saved);
      setView("detail");
    } catch {
      setError("Couldn’t save entry. Your draft is still here. Please try again.");
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm("Delete this journal entry?")) return;
    setError(null);
    try {
      await deleteEntry.mutateAsync(id);
      if (selectedEntry?.id === id) {
        setSelectedEntry(null);
        setView("day");
      }
    } catch (e) {
      setError(String(e));
    }
  }

  if (isPending && !registering) {
    return <main className="min-h-screen bg-stone-50" />;
  }

  if (!session || registering) {
    return (
      <main className="min-h-screen bg-stone-50 font-mono flex items-center justify-center">
        <div className="max-w-xs w-full space-y-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-stone-900">smrti</h1>
            <p className="text-stone-500 text-sm mt-1">what is remembered</p>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <input
            type="text"
            placeholder="your name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-stone-200 rounded text-sm focus:outline-none focus:border-stone-400"
          />
          <button
            onClick={handleRegister}
            disabled={busy || !displayName.trim()}
            className="w-full px-4 py-2 bg-stone-900 text-white text-sm rounded hover:bg-stone-700 disabled:opacity-50"
          >
            {busy ? "working..." : "register with passkey"}
          </button>
          <button
            onClick={handleSignIn}
            disabled={busy}
            className="w-full px-4 py-2 border border-stone-300 text-sm rounded hover:bg-stone-100 disabled:opacity-50"
          >
            {busy ? "working..." : "sign in with passkey"}
          </button>
        </div>
      </main>
    );
  }

  if (session.user.isAnonymous) {
    return (
      <main className="min-h-screen bg-stone-50 font-mono flex items-center justify-center">
        <div className="max-w-xs w-full space-y-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-stone-900">smrti</h1>
            <p className="text-stone-500 text-sm mt-1">complete your registration</p>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <input
            type="text"
            placeholder="your name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-stone-200 rounded text-sm focus:outline-none focus:border-stone-400"
          />
          <button
            onClick={handleCompleteRegistration}
            disabled={busy || !displayName.trim()}
            className="w-full px-4 py-2 bg-stone-900 text-white text-sm rounded hover:bg-stone-700 disabled:opacity-50"
          >
            {busy ? "working..." : "set name"}
          </button>
          <button
            onClick={handleSignOut}
            className="w-full text-sm text-stone-500 hover:text-stone-900"
          >
            sign out
          </button>
        </div>
      </main>
    );
  }

  return (
    <main
      ref={main}
      className="days-app min-h-screen bg-stone-50 text-stone-900 p-4 sm:p-8 max-w-6xl mx-auto"
    >
      <header className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-stone-900">smrti</h1>
        <div className="flex gap-3 items-center">
          {view !== "day" && (
            <button
              disabled={busy}
              onClick={() => {
                setView(view === "edit" ? "detail" : "day");
                setError(null);
                if (view === "edit") {
                  setTitle("");
                  setContent("");
                  setMood("");
                } else setSelectedEntry(null);
              }}
              className="text-sm text-stone-500 hover:text-stone-900"
            >
              {view === "edit" ? "Cancel edit" : "Back to Days"}
            </button>
          )}
          <button
            disabled={busy}
            onClick={handleSignOut}
            className="text-sm text-stone-500 hover:text-stone-900"
          >
            sign out
          </button>
        </div>
      </header>

      {error && (
        <p role="alert" className="text-red-700 mb-4 text-sm">
          {error}
        </p>
      )}

      {(view === "new" || view === "edit") && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void handleSave();
          }}
          className="space-y-4 max-w-2xl mx-auto"
        >
          <h2 tabIndex={-1} className="text-2xl font-semibold">
            {view === "edit" ? "Edit journal entry" : "New journal entry"}
          </h2>
          <p className="text-sm text-stone-600">
            {view === "edit"
              ? "Editing keeps the original Written at time. The entry stays in its original day."
              : `Saved with today’s date and time${day !== localDateISO() ? ", not the day you were viewing" : ""}. Your words stay yours.`}
          </p>
          <label className="block" htmlFor="entry-title">
            Title
          </label>
          <input
            id="entry-title"
            disabled={busy}
            required
            maxLength={200}
            type="text"
            placeholder="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-stone-200 rounded text-sm focus:outline-none focus:border-stone-400"
          />
          <label className="block" htmlFor="entry-content">
            Content
          </label>
          <textarea
            id="entry-content"
            disabled={busy}
            required
            placeholder="what's on your mind?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={8}
            className="w-full px-3 py-2 bg-white border border-stone-200 rounded text-sm focus:outline-none focus:border-stone-400 resize-none"
          />
          <p className="text-sm text-stone-600">Mood (optional)</p>
          <div role="group" aria-label="Mood (optional)" className="flex flex-wrap gap-2">
            {MOODS.map((m) => (
              <button
                key={m.value}
                type="button"
                disabled={busy}
                aria-pressed={mood === m.value}
                onClick={() => setMood(mood === m.value ? "" : m.value)}
                className={`px-3 py-1 text-xs rounded border transition-colors ${
                  mood === m.value
                    ? "bg-stone-900 text-white border-stone-900"
                    : "border-stone-200 text-stone-600 hover:border-stone-400"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <button
            type="submit"
            disabled={busy || !title.trim() || !content.trim()}
            className="px-4 py-2 bg-stone-900 text-white text-sm rounded hover:bg-stone-700 disabled:opacity-50"
          >
            {busy ? "saving..." : view === "edit" ? "Save changes" : "save"}
          </button>
        </form>
      )}

      {view === "detail" && selectedEntry && (
        <div className="max-w-2xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 tabIndex={-1} className="text-lg font-bold text-stone-900 break-words">
                {selectedEntry.title}
              </h2>
              {selectedEntry.mood && (
                <span className="text-xs text-stone-500 bg-stone-100 px-2 py-0.5 rounded">
                  {selectedEntry.mood}
                </span>
              )}
            </div>
            <p className="text-stone-600 text-sm mb-4">
              Written at{" "}
              {new Date(selectedEntry.createdAt).toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </p>
            <div className="bg-white border border-stone-200 rounded p-4 text-sm text-stone-800 whitespace-pre-wrap break-words">
              {selectedEntry.content}
            </div>
          </div>
          <button
            disabled={busy}
            onClick={() => {
              setTitle(selectedEntry.title);
              setContent(selectedEntry.content);
              setMood(selectedEntry.mood ?? "");
              setError(null);
              setView("edit");
            }}
            className="mr-6 rounded border border-stone-300 px-4 text-sm hover:bg-stone-100"
          >
            Edit entry
          </button>
          <button
            disabled={busy}
            onClick={() => handleDelete(selectedEntry.id)}
            className="text-sm text-red-500 hover:text-red-700"
          >
            delete entry
          </button>
        </div>
      )}

      {view === "day" && (
        <DayView
          key={session.user.id}
          userId={session.user.id}
          apiBase={API_BASE}
          date={day}
          onDateChange={setDay}
          onNew={() => setView("new")}
          onSignOut={handleSignOut}
          onOpen={(entry) => {
            setSelectedEntry(entry);
            setView("detail");
          }}
        />
      )}
    </main>
  );
}

export default App;
